import * as React from 'react';
import { Box, InputLabel, SelectChangeEvent, Typography } from '@mui/material';
import { times } from 'lodash';
import { useSearchParams } from 'react-router-dom';
import Slider from '@mui/material/Slider';
import { FilterRowItem } from './styles';
import { getValueAsEvent } from './filters';

const minDiff = 1;
const minAge = 10;
const maxAge = 100;

const steps = (maxAge - minAge) / minDiff + 1;
const marks = times(steps, (index) => {
  const val = index * minDiff + minAge;
  if(val % 5 === 0) {
    return { label: val, value: val };
  }
  return { label: '', value: val };
});

interface Props {
  updateSearch: (key: string, event: SelectChangeEvent<string>) => void;
}
export function AgeRangeFilter({ updateSearch }: Props) {
  const [searchParams] = useSearchParams();

  const handleChange = (
    _: Event,
    newValue: number | number[],
    activeThumb: number
  ) => {
    if (!Array.isArray(newValue)) {
      return;
    }

    let value = [];
    if (newValue[1] - newValue[0] < minDiff) {
      if (activeThumb === 0) {
        const clamped = Math.min(newValue[0], 100 - minDiff);
        value = [clamped, clamped + minDiff];
      } else {
        const clamped = Math.max(newValue[1], minDiff);
        value = [clamped - minDiff, clamped];
      }
    } else {
      value = newValue;
    }
    updateSearch('agerange', getValueAsEvent(value.join(',')));
  };

  const value = React.useMemo(() => {
    const ageRange = searchParams.get('agerange')?.split(',');
    const val = [minAge, maxAge];

    if (ageRange?.[0]) val[0] = parseInt(ageRange[0], 10);
    if (ageRange?.[1]) val[1] = parseInt(ageRange[1], 10);

    return val;
  }, [searchParams]);

  const getTitle = (text: number) => (
    <Typography
      variant="body2"
      className="text-muted select-none pointer-events-none whitespace-nowrap"
      sx={{ minWidth: { xs: 'auto', sm: text === minAge ? 55 : 65 } }}
    >
      {text} Years
    </Typography>
  );

  return (
    <FilterRowItem>
      <InputLabel>Age</InputLabel>
      <Box
        display="flex"
        alignItems="center"
        sx={{ width: "100%", gap: { xs: 2, sm: 4 } }}
      >
        {getTitle(minAge)}
        <Slider
          value={value}
          onChange={handleChange}
          disableSwap
          min={minAge}
          max={maxAge}
          step={minDiff}
          marks={marks}
          valueLabelDisplay="auto"
          sx={{
            width: '100%',
            '& .MuiSlider-markLabel': {
              display: { xs: 'none', sm: 'block' },
            },
            '& .MuiSlider-mark': {
              display: { xs: 'none', sm: 'block' },
            },
          }}
        />
        {getTitle(maxAge)}
      </Box>
    </FilterRowItem>
  );
}
